import { createCache } from '../client/memory-cache';
import { createEvent, createSyncObservable, createAsyncObservable, type AsyncObservable } from '../events';
import { currentUrl, URLX } from '../url';
import { addToDynamicDataCache } from '../dynamic-data';
import * as api from '../client/api';
import * as constants from '../constants';
import { type HistoryEvent, replaceState } from '../client/history';
import { resolveComponentAndChildComponents } from '../resolve-component';
import { getCurrentPage } from '.';
import { ORIGINAL_CACHE_CONTROL_HEADER_NAME } from '../constants';

export type PageDataLoadEvent = HistoryEvent;

export type PageBase = Excite.ContentApi.IComponentData &
  Excite.ContentApi.IPageModel & {
    httpStatus?: number;
    eventId?: string;
    keepComponents?: boolean;
  };

export type PageError = {
  url: URLX;
  errorPage?: Excite.ContentApi.IComponentData;
  response?: Response;
  errorStatus?: number;
  unknownError?: any;
  chunkLoadError?: boolean;
  options?: LoadPageOptions;
  httpStatus?: number;
  eventId?: string;
};

export type PageOrError = PageBase | PageError;

export const fullPageDataCache = createCache<PageBase>(item => item.url, 20);
export const partialPageDataCache = createCache<PageBase>(item => item.url, 100);

export const [currentPage] = createSyncObservable<PageBase | PageError>(undefined as any);
export const [currentPageLoadSuccessful] = createSyncObservable<PageBase>(undefined as any);
export const [isLoading] = createSyncObservable<boolean>(false);
export const [pageLoad] = createEvent<PageDataLoadEvent>();
export const [currentPageLoadFailure] = createSyncObservable<PageError | undefined>(undefined);

export type LoadPageOptions = {
  componentName?: string;
  linkIdentifier?: string;
  shouldFetchData?: boolean | ((cachedData: Excite.ContentApi.IComponentData) => boolean);
  shouldUseCache?: boolean | ((cachedData: Excite.ContentApi.IComponentData) => boolean);
  merge?: (page: PageOrError, newContent: any) => PageOrError;
  extendPartialCache?: (partialCache: PageOrError) => PageBase | undefined;
  keepComponents?: boolean;
};

export async function refreshCurrentPage() {
  await replaceState(currentUrl(), { page: loadPage(currentUrl()) });
}

export function getFullOrPartialCachedPage(url: string) {
  return fullPageDataCache.get(url) ?? partialPageDataCache.get(url);
}

export function clearCachedPages() {
  fullPageDataCache.clear();
  partialPageDataCache.clear();
}

export function setCachedPage<T extends PageBase>(page: T) {
  fullPageDataCache.set(page);
}

let currentLoadingPage: AsyncObservable<PageOrError>;

async function handleApiResponse(
  response: Response,
  url: URLX,
  options: LoadPageOptions | undefined,
  observable: AsyncObservable<PageOrError>,
  firstEmit: Promise<unknown>,
) {
  await firstEmit;
  try {
    let json = await response.json();
    json = options?.merge ? options.merge(getCurrentPage(), json) : json;

    if (isDynamicDataContainer(json)) {
      json.refreshedDynamicData.forEach(dynamicData =>
        addToDynamicDataCache(dynamicData.data, dynamicData.context, dynamicData.type),
      );
    }

    if (isPage(json)) {
      json = normalizePageUrl(json, url);
      json.httpStatus = response.status;
      json.keepComponents = options?.keepComponents;

      findPageAndCacheIfAny(json);
      await emitWhenComponentsAreLoaded(url, observable, json);
      return true;
    }

    observable.emit({ url, response, unknownError: json });
    return false;
  } catch (e) {
    console.trace(e);
    observable.emit({ url, response, unknownError: e });
    return false;
  }
}

function normalizePageUrl(page: Excite.ContentApi.IComponentData, originalUrl: URLX): Excite.ContentApi.IComponentData {
  if (page.url !== decodeURI(originalUrl.pathAndQuery)) {
    const responseUrl = new URLX(page.url);
    originalUrl.hiddenParams.forEach((_, key) => responseUrl.searchParams.delete(key));
    page.url = decodeURI(responseUrl.pathAndQuery);
  }
  return page;
}

async function handleApiError(
  error: api.LoadFailure | TypeError,
  url: URLX,
  options: LoadPageOptions | undefined,
  observable: AsyncObservable<PageOrError>,
  firstEmit: Promise<unknown>,
) {
  if (!isLoadFailure(error)) return;

  await firstEmit;
  let errorPage: Excite.ContentApi.IComponentData | undefined;

  if (error.response.headers.get('content-type') === 'application/json') {
    const json = await error.response.json();
    if (isPage(json)) {
      await resolveComponentAndChildComponents(json);
      errorPage = json;
    }
  }

  observable.emit({
    url,
    response: error.response,
    errorStatus: error.status,
    errorPage,
    options,
  });
}

function isLoadFailure(err: any): err is api.LoadFailure {
  return typeof err.response !== 'undefined' && typeof err.status !== 'undefined';
}

export function loadPage(url: URLX, options?: LoadPageOptions) {
  const [observable, closeObservable] = createAsyncObservable<PageOrError>();
  const urldecoded = decodeURI(url.pathAndQuery);

  let cachedData = getFullOrPartialCachedPage(urldecoded);
  if (!cachedData && options?.componentName) {
    cachedData = {
      componentName: options.componentName,
      cache: 'empty',
      url: urldecoded,
      shouldCache: false,
      trackingPageType: '',
    };
  }

  if (cachedData) {
    cachedData.keepComponents = options?.keepComponents;
  }

  let firstEmit = Promise.resolve<unknown>(undefined);
  if (shouldUseCache(cachedData, options) && cachedData) {
    if (options?.extendPartialCache) {
      cachedData = options.extendPartialCache(cachedData) ?? cachedData;
    }
    firstEmit = emitWhenComponentsAreLoaded(url, observable, cachedData);
  }

  if (shouldFetchData(cachedData, options)) {
    const apiUrl = createApiUrl(url);
    const headers = {
      ...(options?.linkIdentifier ? { [constants.LINK_IDENTIFIER_HEADER]: options.linkIdentifier } : {}),
      [ORIGINAL_CACHE_CONTROL_HEADER_NAME]: url.toString(),
    };

    if (!cachedData) {
      isLoading.emit(true);
    }
    currentLoadingPage = observable;

    api
      .get(apiUrl, headers)
      .then(async response => {
        const success = await handleApiResponse(response, url, options, observable, firstEmit);
        if (success) closeObservable();
      })
      .catch(async error => {
        await handleApiError(error, url, options, observable, firstEmit);
        closeObservable();
      })
      .finally(() => {
        if (currentLoadingPage === observable) {
          isLoading.emit(false);
        }
      });
  } else {
    firstEmit.then(() => closeObservable());
  }

  return observable;
}

function shouldUseCache(cachedData: Excite.ContentApi.IComponentData | undefined, options?: LoadPageOptions): boolean {
  return Boolean(
    cachedData &&
      (typeof options?.shouldUseCache === 'undefined' ||
        options.shouldUseCache === true ||
        (typeof options.shouldUseCache === 'function' && options.shouldUseCache(cachedData))),
  );
}

function shouldFetchData(cachedData: Excite.ContentApi.IComponentData | undefined, options?: LoadPageOptions): boolean {
  return (
    !cachedData ||
    typeof options?.shouldFetchData === 'undefined' ||
    options.shouldFetchData === true ||
    (typeof options.shouldFetchData === 'function' && options.shouldFetchData(cachedData) === true)
  );
}

function createApiUrl(url: URLX): URLX {
  const apiUrl = new URLX(url);
  if (!apiUrl.pathname.startsWith('/api/content/')) {
    apiUrl.pathname = '/api/content' + apiUrl.pathname;
  }
  return apiUrl;
}

export function isPage(o: unknown): o is PageBase {
  return Boolean(o && (o as PageBase).url && (o as PageBase).componentName);
}

export function isPageError(o: unknown): o is PageError {
  const data = o as PageError;
  return Boolean(
    data?.url && (data.chunkLoadError || data.errorPage || data.errorStatus || data.response || data.unknownError),
  );
}

export function isDynamicDataContainer(o: unknown): o is Excite.ContentApi.IDynamicDataContainer {
  return Boolean(o && (o as Excite.ContentApi.IDynamicDataContainer).refreshedDynamicData);
}

export function findPageAndCacheIfAny(o: unknown) {
  if (!o) {
    return;
  }
  if (Array.isArray(o)) {
    for (const i of o) {
      findPageAndCacheIfAny(i);
    }
  }
  if (typeof o === 'object') {
    if (isPage(o)) {
      if (o.cache === 'full') {
        fullPageDataCache.set(o);
      } else if (o.cache === 'partial') {
        partialPageDataCache.set(o);
      }
    }
    for (const k of Object.keys(o!)) {
      findPageAndCacheIfAny((o as any)[k]);
    }
  }
}

function emitWhenComponentsAreLoaded(
  url: URLX,
  observable: AsyncObservable<Excite.ContentApi.IComponentData | PageError>,
  componentData: Excite.ContentApi.IComponentData,
) {
  const loadComponents = resolveComponentAndChildComponents(componentData);
  if (loadComponents) {
    return loadComponents.then(
      () => {
        observable.emit(componentData!);
      },
      () => {
        observable.emit({ url, chunkLoadError: true });
      },
    );
  } else {
    return observable.emit(componentData);
  }
}

function createHeaders(options?: LoadPageOptions) {
  let headers: { [name: string]: string } = {};
  if (options?.linkIdentifier) {
    headers = {
      [constants.LINK_IDENTIFIER_HEADER]: options.linkIdentifier,
    };
  }
  return headers;
}
